import Card from "components/card/Card";
import Checkbox from "components/form/checkbox/Checkbox";
import FormLayout from "components/form/FormLayout";
import Toggle from "components/form/toggle/Toggle";
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { ILoyaltyProgram } from "redux/reducers/models/loyalty";
import { ICategories, IDepartments } from "redux/reducers/models/product";
import "../loyalty.scss";
import SelectNew from "components/select/SelectNew";
import { TCustomerType } from "redux/reducers/models/facility";
import useDebounce from "hooks/events/useDebounce";
import axios, { CancelToken } from "axios";
import { StatusCode } from "api/protocols";
import { useAppDispatch } from "hooks/redux";
import { showError } from "redux/actions/ui";
import { GetCustomerType } from "api/rpc/2024-04/facilityAdmin/customer/type";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";

interface IProps {
    loyaltyProgram: ILoyaltyProgram;
    setLoyaltyProgram: React.Dispatch<React.SetStateAction<ILoyaltyProgram>>;
    departments: Array<IDepartments & {cateogries?: ICategories[]}>;
    selectedDepartments: Array<number>;
    setSelectedDepartments: React.Dispatch<React.SetStateAction<Array<number>>>;
    enrollAll: boolean;
    setEnrollAll: React.Dispatch<React.SetStateAction<boolean>>;
}


export default function LoyaltySettings(props: IProps) {
    const {loyaltyProgram, setLoyaltyProgram, departments, selectedDepartments, setSelectedDepartments, enrollAll, setEnrollAll} = props;
    const {t} = useTranslation();
    const [customerTypes, setCustomerTypes] = useState<TCustomerType[]>(undefined);
    const [customerTypeSearch, setCustomerTypeSearch] = useState("");
    const debouncedSearch = useDebounce(customerTypeSearch, 500);
    const dispatch = useAppDispatch();

    useEffect(() => {
        const source = axios.CancelToken.source();
        void loadCustomerTypes(source.token);
        return () => source.cancel();
      }, [debouncedSearch]);

      useEffect(() => {
        if (loyaltyProgram && loyaltyProgram?.enrolment_customer_types && loyaltyProgram?.enrolment_customer_types?.length > 0) {
            setEnrollAll(false);
        }
      }, [loyaltyProgram]);
    
      async function loadCustomerTypes(token?: CancelToken) {
        const typeRes = await GetCustomerType({
          search: debouncedSearch,
          application: "green_fee",
        }, false, token);
    
        if (token && token.reason) {
          return;
        }
        if (typeRes.status !== StatusCode.OK) {
          dispatch(showError(typeof typeRes?.data === "string" ? typeRes?.data : t("Error loading customer types")));
          setCustomerTypes([]);
          return;
        }
    
        setCustomerTypes(typeRes.data);
      }

    function handleToggleChange(e: React.ChangeEvent<HTMLInputElement>) {
        const {id, checked} = e.target;
        ReactDOM.unstable_batchedUpdates(() => {
            if (id === "all_purchase_types_qualify" && checked) {
                //Clear selected departments if all purchase types set to true
                setSelectedDepartments([]);
            } else if (id === "automatic_enrolment" && !checked) {
                ReactDOM.unstable_batchedUpdates(() => {
                    setEnrollAll(true);
                    setLoyaltyProgram(prev => ({...prev, enrolment_customer_types: []}));
                });
            }
            setLoyaltyProgram(prev => ({...prev, [id]: +checked}));
        });
    }

    function addDepartment(department: IDepartments & {cateogries?: ICategories[]} | ICategories){
        let updatedIds = [...selectedDepartments];
        if (updatedIds?.includes(department?.id)) {
            //Remove department and all child categories
            if (department?.type === "department") {
                updatedIds = updatedIds?.filter(updatedId => updatedId !== department?.id && !department?.cateogries?.some(category => category?.id === updatedId));
            } else {
                //Remove category
                updatedIds = updatedIds?.filter(updatedId => updatedId !== department?.id);
            }
        } else {
            //Add department and all child categories
            if (department?.type === "department") {
                const allIds = [department?.id, ...department.cateogries.map(category => category?.id)];
                updatedIds = [...updatedIds, ...allIds];
            } else {
                //Add category
                updatedIds = [...updatedIds, department?.id];
            }
        }
        setSelectedDepartments(updatedIds);
    }

    function handleSelectCustomerType(id: number) {
        let updatedCustomerTypes = loyaltyProgram?.enrolment_customer_types ? [...loyaltyProgram.enrolment_customer_types] : [];
        const foundType = updatedCustomerTypes?.find(typeId => typeId === id);
        if (foundType) {
          updatedCustomerTypes = updatedCustomerTypes?.filter(typeId => typeId !== id);
        } else {
          updatedCustomerTypes.push(id);
        }
        setLoyaltyProgram(prev => ({...prev, enrolment_customer_types: updatedCustomerTypes}));
      }

    return (
        <Card title={t("Settings")} subtitle={t("Manage global settings for your loyalty points program")}>
            <Card.Section title={t("Enrollment Settings")}>
                <FormLayout>
                    <FormLayout.Group>
                        <Toggle 
                            id="automatic_enrolment"
                            onChange={handleToggleChange}
                            checked={!!loyaltyProgram?.automatic_enrolment}
                            labelRight={t("Opt-in customers into program")} 
                            labelSupport={t("Automatically enroll customers into your loyalty program")} />
                    </FormLayout.Group>
                    {loyaltyProgram?.automatic_enrolment ? (
                        <>
                        <FormLayout.Group>
                            <div style={{marginLeft: "60px"}}>
                                <Checkbox size="medium" circle label={t("Enroll all customer types")} checked={enrollAll} onChange={() => {
                                    ReactDOM.unstable_batchedUpdates(() => {
                                        setEnrollAll(true);
                                        setLoyaltyProgram(prev => ({...prev, enrolment_customer_types: []}));
                                    });
                                }}/>
                            </div>
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <div style={{marginLeft: "60px"}}>
                                <Checkbox size="medium" circle label={t("Enroll specific customer types")} checked={!enrollAll} onChange={() => setEnrollAll(false)}/>
                            </div>
                        </FormLayout.Group>
                        </>
                    ) : null}
                    
                    {!enrollAll && loyaltyProgram?.automatic_enrolment ? (
                    <div style={{marginLeft: "60px"}}>
                         <FormLayout.Group>
                         <SelectNew
                             label={t("Opt-in customers into program based on customer types")}
                             id={"new_restriction_customer_type_select"}
                             options={customerTypes?.map(type => ({ id: type.id, label: `${type.title}${type.subtitle ? " - " : ""}${type.subtitle ? type.subtitle : ""}`, extraValue: type }))}
                             onChange={value => handleSelectCustomerType(value)}
                             multiValue={loyaltyProgram?.enrolment_customer_types ?? []}
                             onSearchCallback={(query: string) => setCustomerTypeSearch(query)}
                             searchable
                             showAmountSelected
                             tooltip={t("Automatically enroll all existing and new customers of the selected customer types")}
                         />
                         </FormLayout.Group>
                         <FormLayout.Group>
                         <div className="flex gap-2 items-center">
                             {customerTypes?.filter(type => loyaltyProgram?.enrolment_customer_types?.includes(type?.id))?.map((type, index) => {
                             return (
                                 <div key={index} className="facility-register-settings-tag">
                                 <p className="facility-register-settings-tag-text">{type?.full_title}</p>
                                 <FontAwesomeIcon icon={faXmark} className="facility-register-settings-tag-x" onClick={() => handleSelectCustomerType(type?.id)} />
                                 </div>
                             );
                             })}
                         </div>
                         </FormLayout.Group>
                    </div>
                    ) : null}
                </FormLayout>
            </Card.Section>
            <Card.Section title={t("Earning Rules")}>
                <FormLayout>
                    <FormLayout.Group>
                    <Toggle 
                        id="all_purchase_types_qualify"
                        onChange={handleToggleChange}
                        checked={!!loyaltyProgram?.all_purchase_types_qualify}
                        labelRight={t("All purchase types qualify")} 
                        labelSupport={t("Allow or disallow customers to earn points on all purchases")} />
                    </FormLayout.Group>
                    <FormLayout.Group>
                    <div className="ml-15">
                        <p className="loyalty-settings-title">{t("Excluded Purchase Types")}:</p>
                        {departments?.map((department, index) => {
                            return (
                                <div key={index}>
                                    <Checkbox size="small" label={department?.title} onChange={() => addDepartment(department)} checked={selectedDepartments?.includes(department?.id)} disabled={!!loyaltyProgram?.all_purchase_types_qualify}/>
                                    {department?.cateogries?.length > 0 ? (
                                    department?.cateogries?.map((category, categoryIndex) => {
                                        return (
                                        <div style={{paddingLeft: "24px"}} key={categoryIndex}>
                                            <Checkbox size="small" label={category?.title} onChange={() => addDepartment(category)} checked={selectedDepartments?.includes(category?.id)} disabled={!!loyaltyProgram?.all_purchase_types_qualify}/>
                                        </div>
                                        );
                                    })
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                    </FormLayout.Group>
                    <FormLayout.Group>
                    <Toggle 
                        id="exclude_discounted_items"
                        onChange={handleToggleChange}
                        checked={!!loyaltyProgram?.exclude_discounted_items}
                        labelRight={t("Exclude discounted items")} 
                        labelSupport={t("Disallow customers to earn points from discounted items")} />
                    </FormLayout.Group>
                </FormLayout>
            </Card.Section>
        </Card>
    );
}